import React, { useContext, useEffect, useState } from "react";
import Select from "react-select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getEntities, getMonthlyMerchandisingData, exportMonthlyMerchandising } from "../../API";
import MonthFormField from "../../components/MonthFormField";

function normalizeResponse(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function formatMonthLabel(ym) {
  if (!ym) return "";
  const [year, month] = ym.split("-");
  return new Date(parseInt(year), parseInt(month) - 1).toLocaleString("en", { month: "long", year: "numeric" });
}

function defaultPickerMonth() {
  const now = new Date();
  return [{ month: now.getMonth() + 1, year: now.getFullYear(), monthName: now.toLocaleString("en", { month: "short" }) }];
}

function fromPickerMonth(months) {
  if (!months || months.length === 0) return "";
  const { month, year } = months[0];
  return `${year}-${String(month).padStart(2, "0")}`;
}

function computeObservations(data) {
  const products = data.products;
  const monthLabel = formatMonthLabel(data.month2);
  const above90 = products.filter((p) => p.pct2 >= 90).length;
  const avg = (products.reduce((s, p) => s + p.pct2, 0) / products.length).toFixed(1);
  const observations = [];
  if (above90 === products.length) {
    observations.push(`All ${products.length} SKUs in the top ${products.length} had distribution over 90%.`);
  } else {
    observations.push(`${above90} out of ${products.length} SKUs had distribution over 90% in ${monthLabel}.`);
  }
  observations.push(`Average distribution for ${monthLabel} is at ${avg}% for top performing SKUs.`);
  return observations;
}

const BAR_COLORS = ["#4472C4", "#ED7D31", "#A5A5A5"];

export default function MonthlyMerchandisingReport() {
  const [{ manufacturer, provinces, territories, representatives }] = useContext(ReportParamsContext);

  const [pickerMonth, setPickerMonth] = useState(defaultPickerMonth);
  const month = fromPickerMonth(pickerMonth);
  const [banner, setBanner] = useState(null);
  const [banners, setBanners] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getEntities("banners", "?size=1000").then((res) => setBanners(normalizeResponse(res).sort((a, b) => a.name.localeCompare(b.name))));
  }, []);

  const canGenerate = !!(manufacturer && month);

  const handleGenerate = () => {
    if (!canGenerate) return;
    setLoading(true);
    setData(null);
    setError(null);
    getMonthlyMerchandisingData({
      manufacturerId: manufacturer.id,
      month,
      bannerId: banner?.id,
      provinceIds: provinces?.map((p) => p.id),
      territoryIds: territories?.map((t) => t.id),
      representativeIds: representatives?.map((r) => r.id),
    })
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  const observations = data ? computeObservations(data) : [];

  return (
    <div className="flex-grow-1 overflow-auto p-4">
      <h5 className="mb-4">Monthly Merchandising Update</h5>

      <div className="row g-3 mb-4 align-items-end">
        <div className="col-md-3">
          <MonthFormField
            label="Month"
            value={pickerMonth}
            onChange={setPickerMonth}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">
            Banner <span className="text-muted small">(optional)</span>
          </label>
          <Select
            options={banners}
            getOptionLabel={(o) => o.name}
            getOptionValue={(o) => o.id}
            value={banner}
            onChange={setBanner}
            placeholder="All banners..."
            isClearable
          />
        </div>
        <div className="col-md-2 d-flex gap-2">
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={!canGenerate || loading}
          >
            {loading ? "Generating…" : "Generate"}
          </button>
          <button
            className="btn btn-outline-success"
            onClick={() => {
              if (!canGenerate) return;
              setExporting(true);
              exportMonthlyMerchandising({
                manufacturerId: manufacturer.id,
                month,
                bannerId: banner?.id,
                provinceIds: provinces?.map((p) => p.id),
                territoryIds: territories?.map((t) => t.id),
                representativeIds: representatives?.map((r) => r.id),
              }).catch((err) => setError(err.message)).finally(() => setExporting(false));
            }}
            disabled={!canGenerate || exporting}
          >
            {exporting ? "Exporting…" : "Export"}
          </button>
        </div>
      </div>

      {!manufacturer && (
        <p className="text-muted">Select a manufacturer in the panel above.</p>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {data && data.products.length === 0 && (
        <p className="text-muted">No data found for the selected parameters.</p>
      )}

      {data && data.products.length > 0 && (
        <>
          <div className="card mb-4">
            <div className="card-body">
              <h6 className="card-title text-center fs-5 mb-3">Top 10 Items Distribution</h6>
              <ResponsiveContainer width="100%" height={420}>
                <BarChart
                  data={data.products}
                  margin={{ top: 10, right: 20, bottom: 130, left: 0 }}
                  barCategoryGap="20%"
                  barGap={2}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} width={36} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Legend verticalAlign="top" />
                  <Bar dataKey="pct0" name={formatMonthLabel(data.month0)} fill={BAR_COLORS[0]} />
                  <Bar dataKey="pct1" name={formatMonthLabel(data.month1)} fill={BAR_COLORS[1]} />
                  <Bar dataKey="pct2" name={formatMonthLabel(data.month2)} fill={BAR_COLORS[2]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <p className="fw-semibold fst-italic mb-2">Observations &amp; Opportunities:</p>
              <ul className="mb-2 small">
                {observations.map((obs, i) => <li key={i}>{obs}</li>)}
              </ul>
              <p className="text-muted small mb-0">
                <em>Calculation: Distribution % = # stores where product has facing &gt; 0 / # stores visited × 100</em>
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
